import React, { useState, useEffect, useCallback } from 'react';
import { getData, postData, patchData, deleteData } from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import PolicyTab from '../../../components/common/PolicyTab';
import { useAuth } from '../../../context/AuthContext';

const MONTHS = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_MAP = {
  draft:    { color: 'secondary', label: 'Draft' },
  pending:  { color: 'warning',   label: 'Pending' },
  approved: { color: 'info',      label: 'Approved' },
  paid:     { color: 'success',   label: 'Paid' },
};

const RATING_LABELS = ['', 'Poor', 'Below Average', 'Average', 'Good', 'Excellent'];

const EMPTY_SALARY = {
  employee: '', month: '', year: new Date().getFullYear(),
  basicSalary: '', workingDays: 26, presentDays: '', halfDays: 0, absentDays: 0, leaveDays: 0,
  allowances: { hra: '', transport: '', medical: '', other: '' },
  deductions:  { pf: '', tds: '', absent: '', halfDay: '', other: '' },
  paymentMethod: '', remarks: '',
};

const EMPTY_PERF = {
  employee: '', reviewPeriod: '',
  ratings: { teachingQuality: 3, punctuality: 3, communication: 3, teamwork: 3, initiative: 3, overall: 3 },
  strengths: '', areasForImprovement: '', feedback: '',
};

function num(v) { return parseFloat(v) || 0; }

function computeSalary(form) {
  const a = form.allowances || {};
  const d = form.deductions  || {};
  const gross  = num(form.basicSalary) + num(a.hra) + num(a.transport) + num(a.medical) + num(a.other);
  const dedTot = num(d.pf) + num(d.tds) + num(d.absent) + num(d.halfDay) + num(d.other);
  return { gross, dedTot, net: gross - dedTot };
}

function fmtCurrency(v) {
  return '₹' + num(v).toLocaleString('en-IN', { minimumFractionDigits: 2 });
}

function empName(emp) {
  if (!emp) return '—';
  if (typeof emp === 'string') return emp;
  return `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || '—';
}

// ─── Status Update Modal ─────────────────────────────────────────────────────
function StatusModal({ show, record, onClose, onDone }) {
  const [status, setStatus]   = useState('');
  const [method, setMethod]   = useState('');
  const [date, setDate]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  useEffect(() => {
    if (show && record) { setStatus(record.status || 'draft'); setMethod(''); setDate(''); setErr(''); }
  }, [show, record]);

  if (!show || !record) return null;

  const handle = async () => {
    setSaving(true); setErr('');
    try {
      const body = { status };
      if (status === 'paid') { body.paymentDate = date || undefined; body.paymentMethod = method; }
      await patchData(`/hrm/payroll/${record._id}/status`, body);
      onDone();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title"><i className="bi bi-arrow-repeat me-2"></i>Update Status</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {err && <div className="alert alert-danger py-2">{err}</div>}
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
                {Object.entries(STATUS_MAP).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            {status === 'paid' && <>
              <div className="mb-3">
                <label className="form-label">Payment Date</label>
                <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label">Payment Method</label>
                <select className="form-select" value={method} onChange={e => setMethod(e.target.value)}>
                  <option value="">Select...</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="upi">UPI</option>
                </select>
              </div>
            </>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handle} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : 'Update'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── View Salary Modal ────────────────────────────────────────────────────────
function ViewSalaryModal({ show, record, onClose }) {
  if (!show || !record) return null;
  const a = record.allowances || {};
  const d = record.deductions  || {};
  const st = STATUS_MAP[record.status] || { color: 'secondary', label: record.status };

  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title"><i className="bi bi-receipt me-2"></i>Salary Slip</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <strong>Employee:</strong> {empName(record.employee)}<br />
                <strong>Period:</strong> {MONTHS[record.month] || record.month} {record.year}<br />
                <strong>Status:</strong> <span className={`badge bg-${st.color}`}>{st.label}</span>
              </div>
              <div className="col-md-6">
                {record.paymentMethod && <><strong>Method:</strong> {record.paymentMethod}<br /></>}
                {record.paymentDate && <><strong>Paid on:</strong> {new Date(record.paymentDate).toLocaleDateString('en-IN')}<br /></>}
                {record.remarks && <><strong>Remarks:</strong> {record.remarks}</>}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <table className="table table-sm table-bordered">
                  <thead className="table-success"><tr><th colSpan={2}>Earnings</th></tr></thead>
                  <tbody>
                    <tr><td>Basic Salary</td><td className="text-end">{fmtCurrency(record.basicSalary)}</td></tr>
                    {num(a.hra) > 0 && <tr><td>HRA</td><td className="text-end">{fmtCurrency(a.hra)}</td></tr>}
                    {num(a.transport) > 0 && <tr><td>Transport</td><td className="text-end">{fmtCurrency(a.transport)}</td></tr>}
                    {num(a.medical) > 0 && <tr><td>Medical</td><td className="text-end">{fmtCurrency(a.medical)}</td></tr>}
                    {num(a.other) > 0 && <tr><td>Other Allowance</td><td className="text-end">{fmtCurrency(a.other)}</td></tr>}
                    <tr className="fw-bold table-light"><td>Gross Salary</td><td className="text-end">{fmtCurrency(record.grossSalary)}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="col-md-6">
                <table className="table table-sm table-bordered">
                  <thead className="table-danger"><tr><th colSpan={2}>Deductions</th></tr></thead>
                  <tbody>
                    {num(d.pf) > 0 && <tr><td>PF</td><td className="text-end">{fmtCurrency(d.pf)}</td></tr>}
                    {num(d.tds) > 0 && <tr><td>TDS</td><td className="text-end">{fmtCurrency(d.tds)}</td></tr>}
                    {num(d.absent) > 0 && <tr><td>Absent Deduction</td><td className="text-end">{fmtCurrency(d.absent)}</td></tr>}
                    {num(d.halfDay) > 0 && <tr><td>Half Day Deduction</td><td className="text-end">{fmtCurrency(d.halfDay)}</td></tr>}
                    {num(d.other) > 0 && <tr><td>Other Deductions</td><td className="text-end">{fmtCurrency(d.other)}</td></tr>}
                    <tr className="fw-bold table-light"><td>Total Deductions</td><td className="text-end">{fmtCurrency(d.total)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="alert alert-success d-flex justify-content-between align-items-center">
              <strong>Net Salary</strong>
              <span className="fs-4 fw-bold">{fmtCurrency(record.netSalary)}</span>
            </div>

            <div className="row g-2 text-center">
              {[['Working Days', record.workingDays],['Present', record.presentDays],['Half Day', record.halfDays],['Absent', record.absentDays],['Leave', record.leaveDays]].map(([l, v]) => (
                <div className="col" key={l}>
                  <div className="border rounded p-2"><div className="fw-bold">{v ?? '—'}</div><small className="text-muted">{l}</small></div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Performance Review Modal ─────────────────────────────────────────────────
function PerfModal({ show, record, employees, isAdmin, onClose, onDone }) {
  const [form, setForm]   = useState(EMPTY_PERF);
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');

  useEffect(() => {
    if (show) {
      if (record) {
        setForm({
          employee: record.employee?._id || record.employee || '',
          reviewPeriod: record.reviewPeriod || '',
          ratings: record.ratings || EMPTY_PERF.ratings,
          strengths: (record.strengths || []).join('\n'),
          areasForImprovement: (record.areasForImprovement || []).join('\n'),
          feedback: record.feedback || '',
        });
      } else {
        setForm(EMPTY_PERF);
      }
      setErr('');
    }
  }, [show, record]);

  if (!show) return null;

  const setRating = (k, v) => setForm(p => ({ ...p, ratings: { ...p.ratings, [k]: Number(v) } }));

  const handle = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      const payload = {
        ...form,
        strengths: form.strengths.split('\n').map(s => s.trim()).filter(Boolean),
        areasForImprovement: form.areasForImprovement.split('\n').map(s => s.trim()).filter(Boolean),
      };
      if (record) {
        await patchData(`/hrm/payroll/performance/${record._id}`, payload);
      } else {
        await postData('/hrm/payroll/performance', payload);
      }
      onDone();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const RATING_KEYS = [
    ['teachingQuality', 'Teaching Quality'],
    ['punctuality', 'Punctuality'],
    ['communication', 'Communication'],
    ['teamwork', 'Teamwork'],
    ['initiative', 'Initiative'],
    ['overall', 'Overall'],
  ];

  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title"><i className="bi bi-star me-2"></i>{record ? 'Edit' : 'New'} Performance Review</h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <form onSubmit={handle}>
            <div className="modal-body">
              {err && <div className="alert alert-danger py-2">{err}</div>}
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Employee <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.employee} onChange={e => setForm(p => ({ ...p, employee: e.target.value }))} required disabled={!isAdmin}>
                    <option value="">Select employee...</option>
                    {employees.map(e => (
                      <option key={e._id} value={e._id}>{e.firstName} {e.lastName}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Review Period</label>
                  <input className="form-control" placeholder="e.g. Q1 2025 or Jan-Mar 2025" value={form.reviewPeriod}
                    onChange={e => setForm(p => ({ ...p, reviewPeriod: e.target.value }))} />
                </div>

                <div className="col-12">
                  <small className="text-muted fw-bold text-uppercase">Ratings (1–5)</small><hr className="mt-1 mb-2" />
                  <div className="row g-2">
                    {RATING_KEYS.map(([k, label]) => (
                      <div className="col-md-6" key={k}>
                        <label className="form-label mb-1">{label}: <strong>{RATING_LABELS[form.ratings[k]] || form.ratings[k]}</strong></label>
                        <input type="range" className="form-range" min="1" max="5" value={form.ratings[k]}
                          onChange={e => setRating(k, e.target.value)} disabled={!isAdmin} />
                        <div className="d-flex justify-content-between" style={{ fontSize: '0.7rem', color: '#888' }}>
                          <span>Poor</span><span>Excellent</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Strengths <small className="text-muted">(one per line)</small></label>
                  <textarea className="form-control" rows={3} value={form.strengths}
                    onChange={e => setForm(p => ({ ...p, strengths: e.target.value }))} disabled={!isAdmin} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Areas for Improvement <small className="text-muted">(one per line)</small></label>
                  <textarea className="form-control" rows={3} value={form.areasForImprovement}
                    onChange={e => setForm(p => ({ ...p, areasForImprovement: e.target.value }))} disabled={!isAdmin} />
                </div>
                <div className="col-12">
                  <label className="form-label">Reviewer Feedback</label>
                  <textarea className="form-control" rows={3} value={form.feedback}
                    onChange={e => setForm(p => ({ ...p, feedback: e.target.value }))} disabled={!isAdmin} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
              {isAdmin && (
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : <><i className="bi bi-check-lg me-1"></i>Save</>}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Main PayrollPage ─────────────────────────────────────────────────────────
export default function PayrollPage() {
  const { user, isAdmin, isManager } = useAuth();
  const canAdmin = isAdmin || isManager;

  const [tab, setTab]             = useState('salary');
  const [employees, setEmployees] = useState([]);

  // ── Salary state ──
  const [salaries, setSalaries]   = useState([]);
  const [salaryPag, setSalaryPag] = useState({ page: 1, totalPages: 1, total: 0 });
  const [salaryFilters, setSalaryFilters] = useState({ employeeId: '', month: '', year: '', status: '' });
  const [summary, setSummary]     = useState({ totalGross: 0, totalNet: 0, count: 0 });
  const [showSalaryForm, setShowSalaryForm] = useState(false);
  const [salaryForm, setSalaryForm] = useState(EMPTY_SALARY);
  const [statusTarget, setStatusTarget] = useState(null);
  const [viewTarget, setViewTarget]   = useState(null);

  // ── Performance state ──
  const [perfs, setPerfs]           = useState([]);
  const [perfPag, setPerfPag]       = useState({ page: 1, totalPages: 1, total: 0 });
  const [showPerfModal, setShowPerfModal] = useState(false);
  const [editPerf, setEditPerf]     = useState(null);

  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [toast, setToast]         = useState(null);

  // Load employees for dropdowns (admin only)
  useEffect(() => {
    if (!canAdmin) return;
    getData('/users').then(r => setEmployees(r.users || r.data || [])).catch(() => {});
  }, [canAdmin]);

  // ── Salary fetch ──
  const fetchSalaries = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (canAdmin) {
        if (salaryFilters.employeeId) params.employeeId = salaryFilters.employeeId;
        if (salaryFilters.month)      params.month      = salaryFilters.month;
        if (salaryFilters.year)       params.year       = salaryFilters.year;
        if (salaryFilters.status)     params.status     = salaryFilters.status;
      }
      const [r, s] = await Promise.all([
        getData('/hrm/payroll', params),
        canAdmin ? getData('/hrm/payroll/summary', salaryFilters.year ? { year: salaryFilters.year } : {}).catch(() => null) : null,
      ]);
      setSalaries(r.records || []);
      setSalaryPag(r.pagination || { page: 1, totalPages: 1, total: r.records?.length || 0 });
      if (s?.data) setSummary(s.data);
    } catch (e) { setToast({ type: 'danger', message: e.message }); }
    finally { setLoading(false); }
  }, [canAdmin, salaryFilters]);

  // ── Performance fetch ──
  const fetchPerfs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const r = await getData('/hrm/payroll/performance', { page, limit: 15 });
      setPerfs(r.records || []);
      setPerfPag(r.pagination || { page: 1, totalPages: 1, total: r.records?.length || 0 });
    } catch (e) { setToast({ type: 'danger', message: e.message }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'salary') fetchSalaries(1); }, [tab, fetchSalaries]);
  useEffect(() => { if (tab === 'performance') fetchPerfs(1); }, [tab, fetchPerfs]);

  // ── Save Salary ──
  const handleSaveSalary = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { gross, dedTot, net } = computeSalary(salaryForm);
      const a = salaryForm.allowances;
      const d = salaryForm.deductions;
      const payload = {
        employee: salaryForm.employee,
        month: Number(salaryForm.month),
        year: Number(salaryForm.year),
        basicSalary: num(salaryForm.basicSalary),
        workingDays: num(salaryForm.workingDays),
        presentDays: num(salaryForm.presentDays),
        halfDays:    num(salaryForm.halfDays),
        absentDays:  num(salaryForm.absentDays),
        leaveDays:   num(salaryForm.leaveDays),
        allowances: { hra: num(a.hra), transport: num(a.transport), medical: num(a.medical), other: num(a.other) },
        deductions:  { pf: num(d.pf), tds: num(d.tds), absent: num(d.absent), halfDay: num(d.halfDay), other: num(d.other) },
        paymentMethod: salaryForm.paymentMethod,
        remarks: salaryForm.remarks,
      };
      await postData('/hrm/payroll', payload);
      setToast({ type: 'success', message: 'Salary record created.' });
      setShowSalaryForm(false);
      setSalaryForm(EMPTY_SALARY);
      fetchSalaries(1);
    } catch (e) { setToast({ type: 'danger', message: e.message }); }
    finally { setSaving(false); }
  };

  const setAllow = (k, v) => setSalaryForm(p => ({ ...p, allowances: { ...p.allowances, [k]: v } }));
  const setDeduct = (k, v) => setSalaryForm(p => ({ ...p, deductions:  { ...p.deductions,  [k]: v } }));

  const { gross, dedTot, net } = computeSalary(salaryForm);

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {toast && <AlertToast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <PageHeader title="Payroll & Performance" icon="bi bi-cash-coin" subtitle="Manage salary records and performance reviews" />

      {/* Summary Cards */}
      {canAdmin && (
        <div className="row g-3 mb-4">
          <div className="col-sm-4">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <div className="fs-2 fw-bold text-primary">{summary.count}</div>
                <small className="text-muted">Salary Records</small>
              </div>
            </div>
          </div>
          <div className="col-sm-4">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <div className="fs-5 fw-bold text-success">{fmtCurrency(summary.totalGross)}</div>
                <small className="text-muted">Total Gross</small>
              </div>
            </div>
          </div>
          <div className="col-sm-4">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <div className="fs-5 fw-bold text-info">{fmtCurrency(summary.totalNet)}</div>
                <small className="text-muted">Total Net</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'salary' ? 'active' : ''}`} onClick={() => setTab('salary')}>
            <i className="bi bi-cash-coin me-1"></i>Salary Records
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'performance' ? 'active' : ''}`} onClick={() => setTab('performance')}>
            <i className="bi bi-star me-1"></i>Performance Reviews
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'policy' ? 'active' : ''}`} onClick={() => setTab('policy')}>
            <i className="bi bi-journal-text me-1"></i>Policy / Manual
          </button>
        </li>
      </ul>

      {tab === 'policy' && <PolicyTab moduleKey="payroll" label="Payroll Policy / Manual" />}

      {/* ── SALARY TAB ────────────────────────────────────────────────────────── */}
      {tab === 'salary' && (
        <>
          {/* Filters + Add */}
          <div className="card mb-3">
            <div className="card-body py-2">
              <div className="row g-2 align-items-end">
                {canAdmin && (
                  <div className="col-md-3">
                    <select className="form-select form-select-sm" value={salaryFilters.employeeId}
                      onChange={e => setSalaryFilters(p => ({ ...p, employeeId: e.target.value }))}>
                      <option value="">All Employees</option>
                      {employees.map(e => <option key={e._id} value={e._id}>{e.firstName} {e.lastName}</option>)}
                    </select>
                  </div>
                )}
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={salaryFilters.month}
                    onChange={e => setSalaryFilters(p => ({ ...p, month: e.target.value }))}>
                    <option value="">All Months</option>
                    {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <input type="number" className="form-control form-control-sm" placeholder="Year"
                    value={salaryFilters.year} onChange={e => setSalaryFilters(p => ({ ...p, year: e.target.value }))} />
                </div>
                {canAdmin && (
                  <div className="col-md-2">
                    <select className="form-select form-select-sm" value={salaryFilters.status}
                      onChange={e => setSalaryFilters(p => ({ ...p, status: e.target.value }))}>
                      <option value="">All Status</option>
                      {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                )}
                <div className="col-auto">
                  <button className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSalaryFilters({ employeeId: '', month: '', year: '', status: '' })}>
                    <i className="bi bi-x-circle"></i>
                  </button>
                </div>
                {canAdmin && !showSalaryForm && (
                  <div className="col-auto ms-auto">
                    <button className="btn btn-sm btn-primary" onClick={() => { setSalaryForm(EMPTY_SALARY); setShowSalaryForm(true); }}>
                      <i className="bi bi-plus-lg me-1"></i>New Salary Record
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Create Form */}
          {showSalaryForm && canAdmin && (
            <div className="card mb-4 border-primary">
              <div className="card-header bg-primary text-white d-flex justify-content-between">
                <span><i className="bi bi-cash-coin me-2"></i>New Salary Record</span>
                <button className="btn-close btn-close-white" onClick={() => setShowSalaryForm(false)}></button>
              </div>
              <div className="card-body">
                <form onSubmit={handleSaveSalary}>
                  <div className="row g-3">
                    {/* Employee & Period */}
                    <div className="col-md-4">
                      <label className="form-label">Employee <span className="text-danger">*</span></label>
                      <select className="form-select" value={salaryForm.employee}
                        onChange={e => setSalaryForm(p => ({ ...p, employee: e.target.value }))} required>
                        <option value="">Select employee...</option>
                        {employees.map(e => <option key={e._id} value={e._id}>{e.firstName} {e.lastName}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Month <span className="text-danger">*</span></label>
                      <select className="form-select" value={salaryForm.month}
                        onChange={e => setSalaryForm(p => ({ ...p, month: e.target.value }))} required>
                        <option value="">Select...</option>
                        {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Year <span className="text-danger">*</span></label>
                      <input type="number" className="form-control" value={salaryForm.year}
                        onChange={e => setSalaryForm(p => ({ ...p, year: e.target.value }))} required min="2000" max="2100" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Basic Salary (₹) <span className="text-danger">*</span></label>
                      <input type="number" className="form-control" value={salaryForm.basicSalary}
                        onChange={e => setSalaryForm(p => ({ ...p, basicSalary: e.target.value }))} required min="0" step="0.01" />
                    </div>

                    {/* Attendance */}
                    <div className="col-12"><small className="text-muted fw-bold text-uppercase">Attendance</small><hr className="mt-1 mb-2" /></div>
                    {[['workingDays','Working Days'],['presentDays','Present Days'],['halfDays','Half Days'],['absentDays','Absent Days'],['leaveDays','Leave Days']].map(([k, l]) => (
                      <div className="col-md-2" key={k}>
                        <label className="form-label">{l}</label>
                        <input type="number" className="form-control" value={salaryForm[k]}
                          onChange={e => setSalaryForm(p => ({ ...p, [k]: e.target.value }))} min="0" />
                      </div>
                    ))}

                    {/* Allowances */}
                    <div className="col-12"><small className="text-muted fw-bold text-uppercase">Allowances</small><hr className="mt-1 mb-2" /></div>
                    {[['hra','HRA'],['transport','Transport'],['medical','Medical'],['other','Other']].map(([k, l]) => (
                      <div className="col-md-3" key={k}>
                        <label className="form-label">{l} (₹)</label>
                        <input type="number" className="form-control" value={salaryForm.allowances[k]}
                          onChange={e => setAllow(k, e.target.value)} min="0" step="0.01" />
                      </div>
                    ))}
                    <div className="col-md-3">
                      <label className="form-label">Gross Salary</label>
                      <div className="form-control bg-light fw-bold text-success">{fmtCurrency(gross)}</div>
                    </div>

                    {/* Deductions */}
                    <div className="col-12"><small className="text-muted fw-bold text-uppercase">Deductions</small><hr className="mt-1 mb-2" /></div>
                    {[['pf','PF'],['tds','TDS'],['absent','Absent Deduction'],['halfDay','Half Day Deduction'],['other','Other']].map(([k, l]) => (
                      <div className="col-md-2" key={k}>
                        <label className="form-label">{l} (₹)</label>
                        <input type="number" className="form-control" value={salaryForm.deductions[k]}
                          onChange={e => setDeduct(k, e.target.value)} min="0" step="0.01" />
                      </div>
                    ))}
                    <div className="col-md-2">
                      <label className="form-label">Total Deductions</label>
                      <div className="form-control bg-light fw-bold text-danger">{fmtCurrency(dedTot)}</div>
                    </div>

                    {/* Net + Options */}
                    <div className="col-12">
                      <div className="alert alert-success d-flex justify-content-between align-items-center py-2 mb-0">
                        <strong><i className="bi bi-currency-rupee me-1"></i>Net Salary</strong>
                        <span className="fs-5 fw-bold">{fmtCurrency(net)}</span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Payment Method</label>
                      <select className="form-select" value={salaryForm.paymentMethod}
                        onChange={e => setSalaryForm(p => ({ ...p, paymentMethod: e.target.value }))}>
                        <option value="">Select...</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="cash">Cash</option>
                        <option value="cheque">Cheque</option>
                        <option value="upi">UPI</option>
                      </select>
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Remarks</label>
                      <input className="form-control" value={salaryForm.remarks}
                        onChange={e => setSalaryForm(p => ({ ...p, remarks: e.target.value }))} />
                    </div>

                    <div className="col-12 d-flex gap-2 justify-content-end">
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setShowSalaryForm(false)}>Cancel</button>
                      <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : <><i className="bi bi-check-lg me-1"></i>Save Record</>}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Salary Table */}
          {loading ? <LoadingSpinner /> : (
            <div className="card shadow-sm">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Employee</th>
                      <th>Period</th>
                      <th>Basic</th>
                      <th>Gross</th>
                      <th>Net Salary</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salaries.length === 0 ? (
                      <tr><td colSpan={7} className="text-center text-muted py-4">No salary records found.</td></tr>
                    ) : salaries.map(r => {
                      const st = STATUS_MAP[r.status] || { color: 'secondary', label: r.status };
                      return (
                        <tr key={r._id}>
                          <td>
                            <div className="fw-semibold">{empName(r.employee)}</div>
                            {r.employee?.roleKey && <small className="text-muted">{r.employee.roleKey}</small>}
                          </td>
                          <td>{MONTHS[r.month] || r.month} {r.year}</td>
                          <td>{fmtCurrency(r.basicSalary)}</td>
                          <td>{fmtCurrency(r.grossSalary)}</td>
                          <td><strong className="text-success">{fmtCurrency(r.netSalary)}</strong></td>
                          <td><span className={`badge bg-${st.color}`}>{st.label}</span></td>
                          <td>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-outline-primary" onClick={() => setViewTarget(r)} title="View Slip">
                                <i className="bi bi-eye"></i>
                              </button>
                              {canAdmin && (
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => setStatusTarget(r)} title="Update Status">
                                  <i className="bi bi-arrow-repeat"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {salaryPag.totalPages > 1 && (
                <div className="card-footer d-flex justify-content-between align-items-center py-2">
                  <small className="text-muted">{salaryPag.total} records</small>
                  <div className="d-flex gap-1">
                    <button className="btn btn-sm btn-outline-secondary" disabled={salaryPag.page <= 1}
                      onClick={() => fetchSalaries(salaryPag.page - 1)}><i className="bi bi-chevron-left"></i></button>
                    <span className="btn btn-sm btn-light disabled">Page {salaryPag.page} / {salaryPag.totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={salaryPag.page >= salaryPag.totalPages}
                      onClick={() => fetchSalaries(salaryPag.page + 1)}><i className="bi bi-chevron-right"></i></button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── PERFORMANCE TAB ──────────────────────────────────────────────────── */}
      {tab === 'performance' && (
        <>
          <div className="d-flex justify-content-end mb-3">
            {canAdmin && (
              <button className="btn btn-primary" onClick={() => { setEditPerf(null); setShowPerfModal(true); }}>
                <i className="bi bi-plus-lg me-1"></i>New Review
              </button>
            )}
          </div>

          {loading ? <LoadingSpinner /> : (
            <div className="row g-3">
              {perfs.length === 0 ? (
                <div className="col-12 text-center text-muted py-5">
                  <i className="bi bi-star fs-1 d-block mb-2"></i>No performance reviews yet.
                </div>
              ) : perfs.map(r => {
                const ratings = r.ratings || {};
                const avgRating = Object.values(ratings).reduce((s, v) => s + (v || 0), 0) / Object.keys(ratings).length;
                const stars = Math.round(avgRating);
                return (
                  <div className="col-md-6 col-xl-4" key={r._id}>
                    <div className="card h-100 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex justify-content-between mb-2">
                          <div>
                            <div className="fw-semibold">{empName(r.employee)}</div>
                            <small className="text-muted">{r.reviewPeriod || 'No period set'}</small>
                          </div>
                          <div className="text-warning fs-5">
                            {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
                          </div>
                        </div>
                        <div className="row g-1 mb-2">
                          {Object.entries(ratings).map(([k, v]) => (
                            <div className="col-6" key={k}>
                              <small className="text-muted text-capitalize">{k.replace(/([A-Z])/g, ' $1')}: </small>
                              <small className="fw-semibold">{RATING_LABELS[v] || v}</small>
                            </div>
                          ))}
                        </div>
                        {r.feedback && <p className="small text-muted mb-2" style={{ maxHeight: 60, overflow: 'hidden' }}>{r.feedback}</p>}
                        {(r.strengths?.length > 0) && (
                          <div className="mb-1">
                            {r.strengths.slice(0, 2).map((s, i) => (
                              <span key={i} className="badge bg-success-subtle text-success me-1 mb-1">{s}</span>
                            ))}
                          </div>
                        )}
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <small className="text-muted">By {empName(r.reviewer)}</small>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => { setEditPerf(r); setShowPerfModal(true); }}>
                            <i className="bi bi-eye me-1"></i>View
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {perfPag.totalPages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <button className="btn btn-sm btn-outline-secondary" disabled={perfPag.page <= 1}
                onClick={() => fetchPerfs(perfPag.page - 1)}><i className="bi bi-chevron-left"></i></button>
              <span className="btn btn-sm btn-light disabled">Page {perfPag.page} / {perfPag.totalPages}</span>
              <button className="btn btn-sm btn-outline-secondary" disabled={perfPag.page >= perfPag.totalPages}
                onClick={() => fetchPerfs(perfPag.page + 1)}><i className="bi bi-chevron-right"></i></button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <StatusModal
        show={!!statusTarget} record={statusTarget}
        onClose={() => setStatusTarget(null)}
        onDone={() => { setStatusTarget(null); fetchSalaries(salaryPag.page); }}
      />
      <ViewSalaryModal
        show={!!viewTarget} record={viewTarget}
        onClose={() => setViewTarget(null)}
      />
      <PerfModal
        show={showPerfModal} record={editPerf}
        employees={employees} isAdmin={canAdmin}
        onClose={() => { setShowPerfModal(false); setEditPerf(null); }}
        onDone={() => { setShowPerfModal(false); setEditPerf(null); fetchPerfs(perfPag.page); setToast({ type: 'success', message: 'Performance review saved.' }); }}
      />
    </div>
  );
}
